import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useHuntStore } from '@/stores/huntStore';
import { huntService } from '@/services/api/huntService';
import { ParticipantRole, HuntStatus } from '@gps-hunt/shared';
import { Button, StickyFooter } from '@/components/common';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

/**
 * Create Hunt Flow - Form to create a new hunt with name and description
 * Note: User is already authenticated (protected route)
 */
export const CreateHuntFlow: React.FC = () => {
  const navigate = useNavigate();
  const { displayName: authDisplayName } = useAuth();
  const { setCurrentHunt, setMyRole } = useHuntStore();

  const [huntName, setHuntName] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!huntName.trim()) {
      setError('Hunt name is required');
      return;
    }

    if (huntName.length > 50) {
      setError('Hunt name must be 50 characters or less');
      return;
    }

    if (description.length > 200) {
      setError('Description must be 200 characters or less');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const result = await huntService.createHunt(
        huntName.trim(),
        description.trim() || undefined,
        authDisplayName || 'Creator'
      );
      
      // Update store
      setCurrentHunt({
        id: result.huntId,
        joinCode: result.joinCode,
        status: result.status as HuntStatus,
        pinOrder: [],
        hidersReady: [],
        completedPinIds: [],
        createdAt: result.createdAt,
        createdBy: '',
        name: huntName.trim(),
        creatorDisplayName: authDisplayName || 'Creator',
      });
      setMyRole(ParticipantRole.HIDER);

      // Navigate to lobby
      navigate(`/lobby/${result.huntId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create hunt');
      setIsCreating(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Scrollable content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-md mx-auto w-full">
        <h1 className="text-2xl font-bold text-text-primary mb-2">Create a Hunt</h1>
        <p className="text-text-secondary mb-6">
          Give your hunt a name and optional description so participants know what they&apos;re joining.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="huntName" className="block text-sm font-medium text-text-secondary mb-2">
              Hunt Name <span className="text-red-400">*</span>
            </label>
            <input
              id="huntName"
              type="text"
              value={huntName}
              onChange={(e) => setHuntName(e.target.value)}
              placeholder="e.g., Birthday Scavenger Hunt"
              className="w-full px-4 py-3 bg-surface rounded-lg border border-surface-light 
                         text-text-primary placeholder-text-muted
                         focus:outline-none focus:border-primary/50"
              maxLength={50}
              disabled={isCreating}
            />
            <div className="text-xs text-text-muted mt-1 text-right">{huntName.length}/50</div>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-text-secondary mb-2">
              Description <span className="text-text-muted">(optional)</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell participants what to expect..."
              className="w-full px-4 py-3 bg-surface rounded-lg border border-surface-light 
                         text-text-primary placeholder-text-muted resize-none
                         focus:outline-none focus:border-primary/50"
              rows={3}
              maxLength={200}
              disabled={isCreating}
            />
            <div className="text-xs text-text-muted mt-1 text-right">{description.length}/200</div>
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </form>
        </div>
      </div>

      {/* Sticky footer with action buttons */}
      <StickyFooter>
        <div className="flex gap-3 w-full">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/welcome')}
            disabled={isCreating}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={isCreating || !huntName.trim()}
            className="flex-1"
          >
            {isCreating ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner />
                Creating...
              </span>
            ) : (
              'Create Hunt'
            )}
          </Button>
        </div>
      </StickyFooter>
    </div>
  );
};

export default CreateHuntFlow;
